import axios from 'axios';

/**
 * Get CSRF token from meta tag
 */
const getCsrfToken = () => {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
};
/**
 * Get brand ID from gon
 */
const getBrandId = () => {
  return window.gon?.current_brand || '';
};

const getAnonymousId = () => {
  if (window.gon?.anonymous_id) return window.gon.anonymous_id;
  const match = document.cookie.match(/(?:^|; )anonymous_id=([^;]*)/);
  if (match) return decodeURIComponent(match[1]);
  if (window.rudderanalytics?.getAnonymousId) return window.rudderanalytics.getAnonymousId();
  return crypto.randomUUID();
};

const SignUpService = {
  /**
   * Create a new user account via Devise registration
   * @param {Object} formData - The form data from TravelAdvisorSignUpForm
   * @param {string} formData.email - User's email address
   * @param {string} formData.firstName - User's first name
   * @param {string} formData.lastName - User's last name
   * @param {string} formData.country - User's country code
   * @param {string} formData.mobilePhone - User's mobile phone number
   * @param {string} formData.password - User's password
   * @param {string} formData.confirmPassword - Password confirmation
   * @param {string} formData.consortium - Travel advisor consortium
   * @param {string} formData.agency - Travel advisor agency
   * @param {string} formData.subAgency - Travel advisor sub agency
   * @param {boolean} formData.agreedToTerms - Whether user agreed to terms
   * @returns {Promise<Object>} - The response data from the server
   */
  createUser: async (formData) => {
    const config = {
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': getCsrfToken(),
        'X-Requested-With': 'XMLHttpRequest'
      }
    };

    const OTHER_VALUE = '__OTHER__';
    const sanitizeId = (val) => (val === OTHER_VALUE ? '' : val);

    // Transform form data to match Devise controller expected params
    const userData = {
      user: {
        brand_id: getBrandId(),
        email: formData.email,
        first_name: formData.firstName,
        last_name: formData.lastName,
        country_code: formData.country,
        phone_number: formData.mobilePhone,
        password: formData.password,
        terms_and_conditions_accepted: formData.agreedToTerms,
        is_active: true,
        has_temp_pass: false,
        // Travel advisor specific fields — sanitize sentinel values
        consortium: sanitizeId(formData.consortium),
        agency: sanitizeId(formData.agency),
        sub_agency: sanitizeId(formData.subAgency),
        travel_advisor_id: formData.travelAdvisorId,
        // "Other" entry custom names
        other_consortium_name: formData.otherConsortiumName || '',
        other_agency_name: formData.otherAgencyName || '',
        other_sub_agency_name: formData.otherSubAgencyName || ''
      },
      anonymous_id: getAnonymousId(),
      // Required empty field for spam check
      a_comment_body: ''
    };

    const response = await axios.post('/users/create_user', userData, config);
    return response.data;
  },

  registerAdvisor: async (formData) => {
    const config = {
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': getCsrfToken(),
        'X-Requested-With': 'XMLHttpRequest'
      }
    };

    const advisorData = {
      email: formData.email,
      first_name: formData.firstName,
      last_name: formData.lastName,
      phone: formData.mobilePhone,
      agency_id: formData.subAgency || formData.agency
    };

    const response = await axios.post('/api/v6/travel_agent/advisors/register', advisorData, config);
    return response.data;
  }
};

export default SignUpService;
